/**
 * ★ O FORMATADOR do número que a voz fala — [spec-008 T044], D-O-3.
 *
 * Módulo PURO: sem rede, sem DOM, sem relógio. Mesmo espírito de
 * `medicao-de-voz.js` — entra número, sai string, e por isso a régua dele são
 * golden numbers e não um ambiente montado.
 *
 * ## Por que ele existe: a diferença é QUEM arredonda
 *
 * A D-13 dizia que a voz **remetia** ao cartão e não falava valor nenhum. O
 * owner decidiu em 2026-09-17 (D-O-3) que ela **fala** — arredondado por NÓS.
 * A troca não afrouxa a guarda, ela a torna verificável:
 *
 * | | antes (v1, medido) | agora |
 * |---|---|---|
 * | quem arredonda | **o modelo**, por conta própria: `1308814.05` virou *"aproximadamente 1,3 milhão"* em **2 de 4** execuções — nas outras 2, não | **nós**, por esta função |
 * | testável | não: depende do humor do modelo | **sim**: o falado é comparado com a saída daqui |
 *
 * ## ⚠️ O risco que ESTE módulo NÃO elimina — e por isso o cartão continua
 *
 * O portão G-14 mediu que **o áudio pode divergir do próprio transcript**: 2 em
 * 31 sessões, uma delas num **dígito de percentual** (4,2 falado como 4,4).
 * Nenhum teste deste arquivo cobre isso, porque a divergência acontece depois
 * da nossa última linha de código — dentro da síntese de voz do provedor.
 *
 * O que arredondar faz é **encolher o dano**, não removê-lo: errar o terceiro
 * dígito de *"1,31 milhão"* custa 0,8%; errar um dígito de *"um milhão,
 * trezentos e oito mil, oitocentos e quatorze"* pode custar qualquer coisa.
 * **É por isso que o valor EXATO continua no cartão** (`SAIDA_*.onde`), com
 * cliente, ativo, período e data-base — a fonte de verdade auditável é a tela,
 * e a fala é conveniência. Quem for tentado a tirar o valor do cartão porque
 * "a voz já diz", leia este parágrafo de novo.
 *
 * ## A regra: 3 dígitos SIGNIFICATIVOS, não 3 casas decimais
 *
 * `1.308.814,05` → *"1,31 milhão"*; `14,37%` → *"14,4%"*; `2.347.891,23` →
 * *"2,35 milhões"*. E **a magnitude nunca muda** — este é o invariante duro.
 */

/** Os três tipos de valor. Fora desta lista, cai em `TIPO_NUMERO` (ver abaixo). */
export const TIPO_NUMERO = 'numero'
export const TIPO_MONETARIO = 'monetario'
export const TIPO_PERCENTUAL = 'percentual'

export const TIPOS_DE_VALOR = Object.freeze([
  TIPO_NUMERO,
  TIPO_MONETARIO,
  TIPO_PERCENTUAL,
])

/** O contrato da D-O-3, nomeado — nunca o literal `3` espalhado pelo arquivo. */
export const DIGITOS_SIGNIFICATIVOS = 3

/**
 * ★ A palavra de escala entra só a partir do MILHÃO.
 *
 * O milhar fica de fora de propósito: *mil* é invariável em PT-BR e ninguém
 * fala *"um vírgula trinta e um mil"* — fala *"mil trezentos e dez"*. Então a
 * faixa do milhar sai com os dígitos (`1.310`), que é o mesmo valor, com os
 * mesmos 3 dígitos significativos, na forma que se diz em voz alta.
 */
const ESCALAS = [
  { limite: 1e12, singular: 'trilhão', plural: 'trilhões' },
  { limite: 1e9, singular: 'bilhão', plural: 'bilhões' },
  { limite: 1e6, singular: 'milhão', plural: 'milhões' },
]

const PREFIXO_NEGATIVO = 'menos '
const PREFIXO_MONETARIO = 'R$ '
const SUFIXO_PERCENTUAL = '%'

/** A ausência de valor. Uma constante porque três caminhos a devolvem. */
export const SEM_VALOR = ''

/**
 * ★ Aceita `number` e string em formato de MÁQUINA (`"1308814.05"`), porque
 * JSON traz número como string com frequência.
 *
 * **Recusa string em pt-BR** (`"1.308.814,05"`), e isso é decisão, não
 * esquecimento: `"1.308"` tanto é mil trezentos e oito quanto um vírgula
 * trezentos e oito, e escolher um dos dois seria adivinhar o valor — que é
 * exatamente o que esta task existe para impedir. Sem número legível, a voz
 * volta a REMETER ao cartão, que é o comportamento anterior e é seguro.
 */
const _numeroFinito = (valor) => {
  if (typeof valor === 'number') return isFinite(valor) ? valor : null
  if (typeof valor !== 'string') return null
  const limpo = valor.trim()
  if (!limpo) return null
  const numero = Number(limpo)
  return isFinite(numero) ? numero : null
}

/**
 * Arredonda para `DIGITOS_SIGNIFICATIVOS` via `toPrecision`, que é o caminho
 * que não acumula o erro de ponto flutuante de `Math.round(v * 10^k) / 10^k`
 * (`131 / 0.0001` devolve `1310000.0000000002`).
 */
const _arredondar = (valor) => {
  if (valor === 0) return 0
  return Number(valor.toPrecision(DIGITOS_SIGNIFICATIVOS))
}

/**
 * Quantas casas decimais sobram depois dos dígitos inteiros. Negativa quando o
 * número já tem mais dígitos inteiros do que o orçamento — daí o piso em zero.
 */
const _casasDecimais = (absoluto) => {
  if (absoluto === 0) return 0
  const expoente = Math.floor(Math.log10(absoluto))
  return Math.max(0, DIGITOS_SIGNIFICATIVOS - 1 - expoente)
}

/** `1310` → `1.310`; `45700` → `45.700`. Só a parte inteira é agrupada. */
const _agruparMilhares = (inteiro) =>
  inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

/**
 * Renderiza em pt-BR sem notação científica e **sem zero à direita**: a D-O-3
 * pede *no máximo* 3 significativos, então `1,50` sai `1,5` e `10,0` sai `10`,
 * que é como se fala.
 */
const _emPortugues = (absoluto) => {
  const texto = absoluto.toFixed(_casasDecimais(absoluto))
  const partes = texto.split('.')
  const inteiro = _agruparMilhares(partes[0])
  const decimal = (partes[1] || '').replace(/0+$/, '')
  return decimal ? `${inteiro},${decimal}` : inteiro
}

/** A escala cujo limite o valor JÁ ARREDONDADO alcança — ou nenhuma. */
const _escalaDe = (absoluto) => {
  for (let i = 0; i < ESCALAS.length; i += 1) {
    if (absoluto >= ESCALAS[i].limite) return ESCALAS[i]
  }
  return null
}

/**
 * ★ O invariante da magnitude, em uma linha de código: a escala é escolhida
 * **depois** do arredondamento, sobre o valor arredondado.
 *
 * Sem isso, `999999` — que arredondado a 3 significativos É `1.000.000` —
 * sairia como *"1.000 mil"*, e o que a voz diz deixaria de ser o número que o
 * formatador produziu.
 */
const _comEscala = (absoluto) => {
  const escala = _escalaDe(absoluto)
  if (!escala) return _emPortugues(absoluto)
  const mantissa = absoluto / escala.limite
  // Singular até 2, como em PT-BR: "1,31 milhão", "1,99 milhão", "2 milhões".
  const palavra = mantissa < 2 ? escala.singular : escala.plural
  return `${_emPortugues(mantissa)} ${palavra}`
}

/**
 * ★ Formata o valor que a voz pode falar — [T044], D-O-3.
 *
 * @param {number|string} valor o que a tool devolveu
 * @param {Object} [opcoes]
 * @param {string} [opcoes.tipo] `numero` (default) | `monetario` | `percentual`.
 *   Tipo desconhecido cai em `numero` de propósito: o número continua certo e
 *   só a unidade falta — dizer MENOS é sempre preferível a dizer errado.
 * @returns {string} a fala, ou `SEM_VALOR` quando não há número legível — e aí
 *   quem chama volta à remissão pura, que é o comportamento anterior à D-O-3.
 */
export const formatarParaFala = (valor, opcoes) => {
  const numero = _numeroFinito(valor)
  if (numero === null) return SEM_VALOR

  const tipo = (opcoes && opcoes.tipo) || TIPO_NUMERO
  const arredondado = _arredondar(numero)
  const absoluto = Math.abs(arredondado)

  // ★ Percentual NUNCA ganha palavra de escala: "1,31 milhão por cento" não é
  // português, e um percentual dessa ordem é justamente o que precisa soar
  // estranho para o consultor desconfiar.
  const corpo =
    tipo === TIPO_PERCENTUAL ? _emPortugues(absoluto) : _comEscala(absoluto)

  // O sinal vai por EXTENSO e antes de tudo: `-` em síntese de voz tanto é
  // lido "menos" quanto engolido, e engolir o sinal inverte o fato.
  const sinal = arredondado < 0 ? PREFIXO_NEGATIVO : ''
  if (tipo === TIPO_PERCENTUAL) return `${sinal}${corpo}${SUFIXO_PERCENTUAL}`
  if (tipo === TIPO_MONETARIO) return `${sinal}${PREFIXO_MONETARIO}${corpo}`
  return `${sinal}${corpo}`
}
